/**
 * Roster lookup helpers shared by Roster and DepthChart agents.
 *
 * `fetchRosterForInjury` pulls a slimmed-down roster (id, name, number,
 * nickname) so we can match natural-language references to a single
 * `players` row before mutating injury status or formation assignments.
 */

import { supabase } from '../../../lib/supabase';
import { extractJerseyNumber } from './parsers';

/** Player identifier: number, first+last, or a single name token. */
export interface PlayerIdentifier {
  firstName?: string;
  lastName?: string;
  number?: number;
  /** Single name token when user says e.g. "mark Mike out" or "activate Rocket". */
  name?: string;
}

/** Minimal roster row used during natural-language player lookup. */
export interface RosterPlayerForInjury {
  id: string;
  first_name: string;
  last_name: string;
  number?: number;
  nickname?: string | null;
}

/** Fetch slim roster + nicknames so we can match free-form references. */
export async function fetchRosterForInjury(teamId: string): Promise<RosterPlayerForInjury[]> {
  const { data: playersData, error: playersError } = await supabase
    .from('players')
    .select('id, first_name, last_name, number')
    .eq('team_id', teamId);
  if (playersError || !playersData) return [];

  const { data: cardsData } = await supabase
    .from('player_cards')
    .select('player_id, nickname')
    .eq('team_id', teamId);
  const nicknameByPlayerId = new Map<string, string | null>();
  (cardsData || []).forEach((row: { player_id: string; nickname: string | null }) => {
    nicknameByPlayerId.set(row.player_id, row.nickname ?? null);
  });

  return (playersData as RosterPlayerForInjury[]).map((p) => ({
    ...p,
    nickname: nicknameByPlayerId.get(p.id) ?? undefined,
  }));
}

/** Match a roster against a free-form identifier. May return 0, 1, or many. */
export function findPlayersForInjury(
  roster: RosterPlayerForInjury[],
  identifier: PlayerIdentifier
): RosterPlayerForInjury[] {
  if (identifier.number != null) {
    return roster.filter((p) => p.number === identifier.number);
  }
  const first = (identifier.firstName || '').trim().toLowerCase();
  const last = (identifier.lastName || '').trim().toLowerCase();
  const singleName = (identifier.name || '').trim().toLowerCase();

  // Full first + last: match both
  if (first && last) {
    return roster.filter(
      (p) =>
        p.first_name.toLowerCase() === first &&
        p.last_name.toLowerCase() === last
    );
  }

  // Single token: match against first name, last name, or nickname
  const token = first || last || singleName;
  if (token) {
    return roster.filter((p) => {
      const pFirst = p.first_name.toLowerCase();
      const pLast = p.last_name.toLowerCase();
      const pNick = (p.nickname || '').toLowerCase();
      return pFirst === token || pLast === token || pNick === token;
    });
  }

  return [];
}

/** Convert a free-form player reference (e.g. "John Doe", "#5") into an identifier. */
export function playerRefToIdentifier(playerRef: string): PlayerIdentifier {
  const numMatch = playerRef.match(/^#?\s*(\d{1,3})\s*$/);
  if (numMatch) return { number: parseInt(numMatch[1], 10) };
  const parts = playerRef.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  if (parts.length === 1) return { name: parts[0] };
  return {};
}

/**
 * Extract a player identifier from an injury-status message. Handles all
 * the natural phrasings the chat accepts ("Mark John out", "set #5 as
 * questionable", "Cole is sick", "activate Rocket", etc.).
 */
export function extractPlayerFromInjuryMessage(text: string): PlayerIdentifier {
  const number = extractJerseyNumber(text);
  if (number != null) return { number };

  const namePart = /([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?/;

  // "Cole is sick and should be marked out" / "Finn is injured" / "X is hurt" / "X is out"
  const isSickInjured = text.match(new RegExp(`\\b${namePart.source}\\s+is\\s+(?:sick|injured|hurt|unavailable|out)(?:\\s+and\\s+should\\s+be\\s+marked\\s+out)?`, 'i'));
  if (isSickInjured) return isSickInjured[2] ? { firstName: isSickInjured[1], lastName: isSickInjured[2] } : { name: isSickInjured[1] };

  // "Cole should be marked out" / "Finn should be marked out"
  const shouldBeMarked = text.match(new RegExp(`\\b${namePart.source}\\s+should\\s+be\\s+marked\\s+(?:out|down)`, 'i'));
  if (shouldBeMarked) return shouldBeMarked[2] ? { firstName: shouldBeMarked[1], lastName: shouldBeMarked[2] } : { name: shouldBeMarked[1] };

  // "mark John Doe out" / "mark John out" / "mark Cole out"
  const markMatch = text.match(/\bmark\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+(?:as\s+)?(?:out|questionable|doubtful|healthy)/i);
  if (markMatch) return markMatch[2] ? { firstName: markMatch[1], lastName: markMatch[2] } : { name: markMatch[1] };

  // "set John out" / "set John Doe as out"
  const setAsMatch = text.match(/\bset\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+(?:as|to)\s+(?:out|questionable|doubtful|healthy)/i);
  if (setAsMatch) return setAsMatch[2] ? { firstName: setAsMatch[1], lastName: setAsMatch[2] } : { name: setAsMatch[1] };
  const setDirectMatch = text.match(/\bset\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+(?:out|questionable|doubtful|healthy)\b/i);
  if (setDirectMatch) return setDirectMatch[2] ? { firstName: setDirectMatch[1], lastName: setDirectMatch[2] } : { name: setDirectMatch[1] };

  // "John is out" / "Smith is out" / "John is questionable"
  const isMatch = text.match(/\b([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+is\s+(?:out|questionable|doubtful|healthy)/i);
  if (isMatch) return isMatch[2] ? { firstName: isMatch[1], lastName: isMatch[2] } : { name: isMatch[1] };

  // "activate Mike" / "make Jake available"
  const activateMatch = text.match(/\bactivate\s+([A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)?)\s*$/i);
  if (activateMatch) {
    const parts = activateMatch[1].trim().split(/\s+/);
    return parts.length === 2 ? { firstName: parts[0], lastName: parts[1] } : { name: activateMatch[1].trim() };
  }
  const makeAvailableMatch = text.match(/\bmake\s+([A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)?)\s+available/i);
  if (makeAvailableMatch) {
    const parts = makeAvailableMatch[1].trim().split(/\s+/);
    return parts.length === 2 ? { firstName: parts[0], lastName: parts[1] } : { name: makeAvailableMatch[1].trim() };
  }

  // "injury for John" / "status for Smith"
  const injuryForMatch = text.match(/(?:injury|status)\s+(?:for|on)\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?/i);
  if (injuryForMatch) return injuryForMatch[2] ? { firstName: injuryForMatch[1], lastName: injuryForMatch[2] } : { name: injuryForMatch[1] };
  const statusForMatch = text.match(/(?:out|questionable|doubtful|healthy)\s+for\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?/i);
  if (statusForMatch) return statusForMatch[2] ? { firstName: statusForMatch[1], lastName: statusForMatch[2] } : { name: statusForMatch[1] };

  return {};
}
